/* eslint-disable max-lines */
// Zustand store already bring memoized data
/* eslint-disable react-hooks/exhaustive-deps */
import type {
  IFilterOptions,
  IOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import capitalize from "lodash/capitalize";
import isEmpty from "lodash/isEmpty";
import { useCallback, useContext, useEffect, useMemo } from "react";

import { formatChecks, getFilter } from "./utils";

import { meetingModeContext } from "context/meeting-mode";
import {
  DependencyType,
  SeverityRating,
  VulnerabilityOrigin,
  VulnerabilityTreatment,
  VulnerabilityVerification,
} from "gql/graphql";
import type {
  VulnerabilityFiltersInput as IFilters,
  ReachabilityStatus,
  Technique,
  VulnerabilityState,
} from "gql/graphql";
import { useAuthz } from "hooks/use-authz";
import type { IFindingInfoResponse } from "pages/finding/vulnerabilities/hooks/types";
import { useFiltersStore } from "pages/finding/vulnerabilities/hooks/use-filters-store";
import {
  updateFindingFilterStore,
  useGetFindingFiltersStore,
} from "pages/group/findings/findings-table/hooks/use-finding-filters-store";
import { useFiltersStorage } from "storage/filters";
import { formatReattack } from "utils/format-helpers";
import { PACKAGE_MANAGER_OPTIONS } from "utils/package-managers";
import { translate } from "utils/translations/translate";

interface IVulnerabilitiesFilterProps {
  readonly finding: IFindingInfoResponse["data"]["finding"];
  readonly groupName: string;
  readonly loading: boolean;
}

const TECHNIQUES = ["CSPM", "DAST", "PTAAS", "RE", "SAST", "SCA", "SCR"];
const TREATMENTS = [...Object.values(VulnerabilityTreatment)];
const SEVERITY_RATINGS = [...Object.values(SeverityRating)].filter(
  (value): boolean => value !== SeverityRating.None,
);
const REATTACK_OPTIONS = [...Object.values(VulnerabilityVerification)].map(
  (value): string =>
    value === VulnerabilityVerification.NotRequested ? "NotRequested" : value,
);

const useVulnerabilitiesFilters = ({
  finding,
  groupName,
  loading,
}: IVulnerabilitiesFilterProps): IUseFilterProps<IFilters> => {
  const { can } = useAuthz();
  const { setValues, getValues } = useFiltersStorage();
  const { meetingMode } = useContext(meetingModeContext);

  const {
    autoFixable: findingAutoFixable,
    where: findingWhere,
    technique: findingTechnique,
  } = useGetFindingFiltersStore(groupName);
  const {
    tags,
    origin,
    search,
    state,
    where,
    minEpss,
    maxEpss,
    minSeverity,
    maxSeverity,
    assignees,
    autoFixable,
    minPriority,
    maxPriority,
    setWhere,
    severityRating,
    treatment,
    reattack,
    reportedAfter,
    reportedBefore,
    technique,
    formatTreatment,
    setAssignees,
    setAutoFixable,
    setDependencyType,
    setMaxEpss,
    setMinEpss,
    setMaxPriority,
    setMinPriority,
    setMaxSeverity,
    setMinSeverity,
    setOrigin,
    setPageIndex,
    setPackageManager,
    setReachabilityStatus,
    setReattack,
    setReportedAfter,
    setReportedBefore,
    setScope,
    setSearch,
    setSeverityRating,
    setState,
    setTags,
    setTechnique,
    setTreatment,
  } = useFiltersStore(`vulnerabilities_filters-${groupName}`);
  const canSeeDraft = can("see_draft_status");
  const searchValue =
    search !== undefined && !isEmpty(search) ? search : undefined;

  useEffect((): void => {
    if (findingWhere !== undefined) {
      setWhere?.(findingWhere);
    }
    if (findingTechnique !== undefined) {
      setTechnique?.(findingTechnique);
    }
    if (findingAutoFixable !== undefined) {
      setAutoFixable?.(findingAutoFixable);
    }
  }, []);

  const filterOptions: IOptionsProps<IFilters>[] = useMemo(
    (): IOptionsProps<IFilters>[] => [
      {
        filterOptions: [
          {
            key: "search",
            label: "Location is",
            type: "text",
            value: where !== undefined && !isEmpty(where) ? where : searchValue,
          },
        ],
        group: "Vulnerability information",
        icon: "file-exclamation",
        label: "Location",
      },
      {
        filterOptions: [
          {
            key: "state",
            label: "Status",
            options: [
              {
                checked: state?.[0] === "VULNERABLE",
                label: "Open",
                value: "VULNERABLE",
              },
              {
                checked: state?.[0] === "SAFE",
                label: "Closed",
                value: "SAFE",
              },
              ...(canSeeDraft && !meetingMode
                ? [
                    {
                      checked: state?.[0] === "SUBMITTED",
                      label: "Submitted",
                      value: "SUBMITTED",
                    },
                    {
                      checked: state?.[0] === "REJECTED",
                      label: "Rejected",
                      value: "REJECTED",
                    },
                  ]
                : []),
            ],
            type: "radioButton",
          },
        ],
        group: "Vulnerability information",
        icon: "file-exclamation",
        label: "Status",
      },
      {
        filterOptions: [
          {
            key: "origin",
            label: translate.t("group.tabs.toe.packages.allPackages.origin"),
            options: Object.values(VulnerabilityOrigin).map(
              (value): IOptions => ({
                checked: origin === value,
                label: capitalize(value),
                value,
              }),
            ),
            type: "radioButton",
          },
        ],
        group: "Vulnerability information",
        icon: "file-exclamation",
        label: translate.t("group.tabs.toe.packages.allPackages.origin"),
      },
      {
        filterOptions: [
          {
            key: "dependencyType",
            label: translate.t(
              "group.tabs.toe.packages.allPackages.dependencyType",
            ),
            options: Object.values(DependencyType).map(
              (value): IOptions => ({
                label: capitalize(value),
                value,
              }),
            ),
            type: "radioButton",
          },
        ],
        group: "Packages",
        icon: "box",
        label: translate.t(
          "group.tabs.toe.packages.allPackages.dependencyType",
        ),
      },
      {
        filterOptions: [
          {
            key: "scope",
            label: translate.t("group.tabs.toe.packages.allPackages.stage"),
            options: [
              { label: "Build", value: "BUILD" },
              { label: "Run", value: "RUN" },
            ],
            type: "radioButton",
          },
        ],
        group: "Packages",
        icon: "box",
        label: translate.t("group.tabs.toe.packages.allPackages.stage"),
      },
      {
        filterOptions: [
          {
            key: "packageManager",
            label: translate.t("group.tabs.toe.packages.allPackages.manager"),
            options: PACKAGE_MANAGER_OPTIONS,
            type: "checkboxes",
          },
        ],
        group: "Packages",
        icon: "box",
        label: translate.t("group.tabs.toe.packages.allPackages.manager"),
      },
      {
        filterOptions: [
          {
            key: "reachabilityStatus",
            label: translate.t(
              "group.tabs.toe.packages.allPackages.reachabilityStatus",
            ),
            options: [
              { label: "Reachable", value: "REACHABLE" },
              {
                label: "Potentially reachable",
                value: "POTENTIALLY_REACHABLE",
              },
              { label: "Latent", value: "LATENT" },
            ],
            type: "checkboxes",
          },
        ],
        group: "Packages",
        icon: "box",
        label: translate.t(
          "group.tabs.toe.packages.allPackages.reachabilityStatus",
        ),
      },
      {
        filterOptions: [
          {
            key: "maxEpss",
            label: "% EPSS range",
            maxValue: maxEpss ?? undefined,
            minValue: minEpss ?? undefined,
            placeholder: "%",
            type: "numberRange",
          },
        ],
        group: "Packages",
        icon: "box",
        label: "%EPSS",
        placeholder: "%",
      },
      {
        filterOptions: [
          {
            hideSearch: true,
            key: "severityRating",
            label: "Severity",
            options: SEVERITY_RATINGS.map(
              (value): IOptions => ({
                checked: severityRating?.includes(value),
                label: capitalize(value),
                value,
              }),
            ),
            type: "checkboxes",
          },
          {
            key: "maxSeverity",
            label: "Or define severity range here",
            maxValue: maxSeverity ?? undefined,
            minValue: minSeverity ?? undefined,
            type: "numberRange",
          },
        ],
        group: "Risk and severity",
        icon: "gauge",
        label: "Severity",
      },
      {
        filterOptions: [
          {
            key: "maxPriority",
            label: "Priority score range",
            maxValue: maxPriority ?? undefined,
            minValue: minPriority ?? undefined,
            placeholder: "%",
            type: "numberRange",
          },
        ],
        group: "Risk and severity",
        icon: "gauge",
        label: "Priority score",
        placeholder: "%",
      },
      {
        filterOptions: [
          {
            key: "reportedAfter",
            label: "Define a report date range here",
            maxValue: reportedBefore ?? undefined,
            minValue: reportedAfter ?? undefined,
            type: "dateRange",
          },
        ],
        group: "Report date",
        icon: "clock",
        label: "Report date",
      },
      {
        filterOptions: [
          {
            hideSearch: true,
            key: "technique",
            label: "Technique",
            options: TECHNIQUES.map(
              (value): IOptions => ({
                checked: technique?.includes(value as Technique),
                label: value,
                value,
              }),
            ),
            type: "checkboxes",
          },
        ],
        group: "Technique",
        icon: "bullseye",
        label: "Technique",
      },
      {
        filterOptions: [
          {
            hideSearch: true,
            key: "treatment",
            label: "Treatment",
            options: TREATMENTS.map(
              (value): IOptions => ({
                checked: treatment?.includes(value),
                label: formatTreatment?.(value) ?? value,
                value: value.toUpperCase(),
              }),
            ),
            type: "checkboxes",
          },
        ],
        group: "Vulnerability management",
        icon: "wrench",
        label: "Treatment",
      },
      {
        filterOptions: [
          {
            hideSearch: true,
            key: "reattack",
            label: "Reattack",
            options: REATTACK_OPTIONS.map(
              (value): IOptions => ({
                checked: reattack?.includes(value),
                label: formatReattack(
                  value === "NotRequested" ? value : capitalize(value),
                ),
                value,
              }),
            ),
            type: "checkboxes",
          },
        ],
        group: "Vulnerability management",
        icon: "wrench",
        label: "Reattack",
      },
      {
        filterOptions: [
          {
            key: "assignees",
            label: "Assignees",
            options: (finding ?? { assignees: [] }).assignees.map(
              (assignee): IOptions => ({
                checked: assignees?.includes(assignee),
                label: assignee.replace("_", " "),
                value: assignee,
              }),
            ),
            type: "select",
          },
        ],
        group: "Vulnerability management",
        icon: "wrench",
        label: "Assignees",
      },
      {
        filterOptions: [
          {
            key: "autoFixable",
            label: "Fix with AI",
            options: [
              { checked: autoFixable, label: "Yes", value: "true" },
              { checked: autoFixable === false, label: "No", value: "false" },
            ],
            type: "radioButton",
          },
        ],
        group: "Vulnerability management",
        icon: "wrench",
        label: "Fix with AI",
      },
      {
        filterOptions: [
          {
            key: "tags",
            label: "Tags",
            options: (
              finding ?? { vulnerabilitiesTags: [] }
            ).vulnerabilitiesTags.map(
              (value): IOptions => ({
                checked: tags?.includes(value),
                label: value,
                value,
              }),
            ),
            type: "checkboxes",
          },
        ],
        group: "Vulnerability management",
        icon: "tag",
        label: "Tags",
      },
    ],
    [
      canSeeDraft,
      finding?.assignees,
      finding?.vulnerabilitiesTags,
      origin,
      reattack?.[0],
      search,
      minEpss,
      maxEpss,
      severityRating?.[0],
      state?.[0],
      technique?.[0],
      minPriority,
      maxPriority,
      assignees?.[0],
      treatment?.[0],
      maxSeverity,
      reportedAfter,
      reportedBefore,
      minSeverity,
      tags?.[0],
      where,
      meetingMode,
      autoFixable,
    ],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("vulnerabilities", groupName) as string | null;
  }, [getValues, groupName]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("vulnerabilities", value, groupName);
    },
    [setValues, groupName],
  );

  const handleRangeFilters = useCallback(
    (filters: readonly IFilterOptions<IFilters>[]): void => {
      const maxEpssValue = getFilter(filters, "maxEpss")?.maxValue;
      const minEpssValue = getFilter(filters, "maxEpss")?.minValue;
      const maxPriorityValue = getFilter(filters, "maxPriority")?.maxValue;
      const minPriorityValue = getFilter(filters, "maxPriority")?.minValue;
      const maxSeverityValue = getFilter(filters, "maxSeverity")?.maxValue;
      const minSeverityValue = getFilter(filters, "maxSeverity")?.minValue;
      const reportAfterValue = getFilter(filters, "reportedAfter")?.minValue;
      const reportBeforeValue = getFilter(filters, "reportedAfter")?.maxValue;

      setMaxEpss?.(Number(maxEpssValue));
      setMinEpss?.(Number(minEpssValue));
      setMaxPriority?.(Number(maxPriorityValue));
      setMinPriority?.(Number(minPriorityValue));
      setMaxSeverity?.(Number(maxSeverityValue));
      setMinSeverity?.(Number(minSeverityValue));
      setReportedAfter?.(reportAfterValue as string);
      setReportedBefore?.(reportBeforeValue as string);
    },
    [
      setMaxSeverity,
      setMaxPriority,
      setMinPriority,
      setMinSeverity,
      setReportedAfter,
      setReportedBefore,
    ],
  );

  const extractFilterValues = (
    filters: readonly IFilterOptions<IFilters>[],
  ): {
    autoFixableValue: string | undefined;
    location: string | undefined;
    assigneesValue: string | undefined;
    tagsValue: string[] | undefined;
    packageManager: string[] | undefined;
    severity: string[] | undefined;
    vulnOrigin: string | undefined;
    scope: string | undefined;
    dependencyType: string | undefined;
    status: string | undefined;
    reachabilityStatus: string[] | undefined;
    reattackValue: string[] | undefined;
    techniqueValues: Technique[] | undefined;
    treatmentValue: VulnerabilityTreatment[] | undefined;
  } => {
    const location = getFilter(filters, "search")?.value;
    const assigneesValue = getFilter(filters, "assignees")?.value;
    const tagsValue = formatChecks(getFilter(filters, "tags")?.options)
      ?.split(",")
      .map((value): string => value.trim());
    const packageManager = formatChecks(
      getFilter(filters, "packageManager")?.options,
    )
      ?.split(",")
      .map((value): string => value.trim());
    const reachabilityStatus = formatChecks(
      getFilter(filters, "reachabilityStatus")?.options,
    )
      ?.split(",")
      .map((value): string => value.trim());
    const severity = formatChecks(getFilter(filters, "severityRating")?.options)
      ?.split(",")
      .map((value): string => value.trim());
    const vulnOrigin = formatChecks(getFilter(filters, "origin")?.options);
    const scope = formatChecks(getFilter(filters, "scope")?.options);
    const dependencyType = formatChecks(
      getFilter(filters, "dependencyType")?.options,
    );
    const status = formatChecks(getFilter(filters, "state")?.options);
    const reattackValue = formatChecks(getFilter(filters, "reattack")?.options)
      ?.split(",")
      .map((value): string => value.trim());
    const techniqueValues = formatChecks(
      getFilter(filters, "technique")?.options,
    )
      ?.split(",")
      .map((value): Technique => value.trim() as Technique);
    const treatmentValue = formatChecks(
      getFilter(filters, "treatment")?.options,
    )
      ?.split(",")
      .map(
        (value): VulnerabilityTreatment =>
          value.trim() as VulnerabilityTreatment,
      );
    const autoFixableValue = formatChecks(
      getFilter(filters, "autoFixable")?.options,
    );

    return {
      assigneesValue,
      autoFixableValue,
      dependencyType,
      location,
      packageManager,
      reachabilityStatus,
      reattackValue,
      scope,
      severity,
      status,
      tagsValue,
      techniqueValues,
      treatmentValue,
      vulnOrigin,
    };
  };

  const updateFilterStates = (
    filterValues: ReturnType<typeof extractFilterValues>,
  ): void => {
    const {
      location,
      assigneesValue,
      autoFixableValue,
      tagsValue,
      packageManager,
      severity,
      vulnOrigin,
      scope,
      dependencyType,
      status,
      reachabilityStatus,
      reattackValue,
      techniqueValues,
      treatmentValue,
    } = filterValues;

    setState?.(
      status === undefined ? undefined : [status as VulnerabilityState],
    );
    setOrigin?.(vulnOrigin);
    setPackageManager?.(packageManager);
    setDependencyType?.(dependencyType as DependencyType);
    setScope?.(scope);
    setSearch?.(location);
    setWhere?.(location);
    setAssignees?.(assigneesValue ?? undefined);
    setTags?.(tagsValue ?? undefined);
    setTreatment?.(treatmentValue);
    setTechnique?.(techniqueValues);
    setReattack?.(reattackValue);
    setSeverityRating?.(severity ?? undefined);
    setReachabilityStatus?.(reachabilityStatus as ReachabilityStatus[]);
    setAutoFixable?.(
      autoFixableValue === undefined ? undefined : autoFixableValue === "true",
    );
  };

  const handleFilters = useCallback(
    (filters: readonly IFilterOptions<IFilters>[]): void => {
      if (!loading) {
        const filterValues = extractFilterValues(filters);
        handleRangeFilters(filters);
        updateFilterStates(filterValues);

        updateFindingFilterStore(groupName, {
          technique: filterValues.techniqueValues,
          where: filterValues.location,
        });

        setPageIndex?.(0);
      }
    },
    [loading],
  );

  return useFilters({
    getValues: handleGetValues,
    onFiltersChange: handleFilters,
    options: filterOptions,
    setValues: handleSetValues,
    variant: "grouped",
  });
};

export { useVulnerabilitiesFilters };
